import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@apollo/client";
import { FormikProps } from "formik";
import { Select, type SelectOption } from "@outpave/ds";
import CardPresetConfigDrawer from "components/admin/CardPresetConfigDrawer";
import Role from "enums/Role";
import { GET_CARD_PRESETS } from "graphql/cardPresets";
import { useCheckUserRole } from "hooks/useCheckUserRole";
import { CardPolicy } from "utils/cards/cardFormUtils";
import {
  applyCardPreset,
  CardPreset,
  cardPresetFromNode,
  CardPresetNode,
  clearCardPreset,
  presetLimitsCaption,
} from "utils/cards/cardPresets";

/** Starts a card's policy from a saved preset. Picking none leaves the policy to the user. */
const CardPresetPicker: React.FC<{ formik: FormikProps<CardPolicy> }> = ({
  formik,
}) => {
  const { t } = useTranslation();
  const canAddPreset = useCheckUserRole(Role.Admin);
  const [addingPreset, setAddingPreset] = useState(false);
  // No `first`: the server then reads every page and returns all presets at once.
  const { data, loading, refetch } = useQuery(GET_CARD_PRESETS);

  const presets: CardPreset[] = useMemo(
    () =>
      (data?.cardPresets?.edges ?? []).map(
        ({ node }: { node: CardPresetNode }) => cardPresetFromNode(node),
      ),
    [data],
  );

  const { presetId, nameOfPreset } = formik.values;

  const options: SelectOption[] = useMemo(() => {
    const listed = presets.map((preset) => ({
      id: preset.id,
      label: preset.presetName,
      caption: presetLimitsCaption(preset) || undefined,
    }));
    // The DS finds a label only in `options`, so a chosen preset reads blank until the query lands.
    if (!presetId || listed.some((option) => option.id === presetId))
      return listed;
    return [{ id: presetId, label: nameOfPreset ?? "" }, ...listed];
  }, [presets, presetId, nameOfPreset]);

  if (!canAddPreset && presets.length === 0 && !presetId) return null;

  return (
    <>
      <Select
        label={t("Preset")}
        placeholder={t("Start from a preset (optional)")}
        searchable
        loading={loading}
        options={options}
        value={presetId ?? null}
        topAction={
          canAddPreset
            ? {
                label: t("Add a new preset"),
                onClick: () => setAddingPreset(true),
              }
            : undefined
        }
        onChange={(id) => {
          if (id === presetId) return;
          const preset = presets.find((item) => item.id === id);
          if (id && !preset) return;
          formik.setValues(
            preset
              ? applyCardPreset(formik.values, preset)
              : clearCardPreset(formik.values),
          );
        }}
        fullWidth
      />
      {addingPreset && (
        <CardPresetConfigDrawer
          open
          handleCloseDrawer={() => setAddingPreset(false)}
          onCreatePresetSuccess={(preset) => {
            refetch();
            formik.setValues(applyCardPreset(formik.values, preset));
          }}
        />
      )}
    </>
  );
};

export default CardPresetPicker;
